import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {fundraisersApi} from '../api';
import {FundraiserStoriesFilter, FundraiserStoriesResponse, CreateFundraiserStoryDTO} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type FundraiserStoriesState = {
  getInfiniteStories: UseInfiniteQueryResult<InfiniteData<FundraiserStoriesResponse>, Error>;
  filters: FundraiserStoriesFilter;
  updateFilter: (newFilter: Partial<FundraiserStoriesFilter>) => void;
  createStoryMutation: UseMutationResult<unknown, Error, {appealId: string; dto: CreateFundraiserStoryDTO}>;
  deleteStoryMutation: UseMutationResult<unknown, Error, string>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useFundraiserStories = (initialFilter: FundraiserStoriesFilter): FundraiserStoriesState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FundraiserStoriesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteStories = useInfiniteQuery<
    FundraiserStoriesResponse,
    Error,
    InfiniteData<FundraiserStoriesResponse>,
    ['fundraiser-stories', 'infinite', Omit<FundraiserStoriesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['fundraiser-stories', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return fundraisersApi.getFundraiserStories({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<FundraiserStoriesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createStoryMutation = useMutation({
    mutationFn: ({appealId, dto}: {appealId: string; dto: CreateFundraiserStoryDTO}) =>
      fundraisersApi.createFundraiserStory(appealId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser-stories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'История сбора успешно создана',
        name: 'createFundraiserStory',
        theme: 'success',
      });
    },
  });

  const deleteStoryMutation = useMutation({
    mutationFn: (storyId: string) => fundraisersApi.deleteFundraiserStory(storyId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser-stories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'История сбора успешно удалена',
        name: 'deleteFundraiserStory',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteStories,
    filters,
    updateFilter,
    createStoryMutation,
    deleteStoryMutation,
  };
};
